/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  Button,
  EmptyState,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { Eye, Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import {
  ActionPolicy,
  PolicyAction,
  PolicyActionCategory,
} from '../../../../types/VerificationTypes';

import ActionsSection from './ActionsSection';
import FailedActionAttemptsTab from './FailedActionAttemptsTab';
import RequirementsSection from './RequirementsSection';


type Tab = 'policies' | 'actions' | 'requirements' | 'failed';


/**
 * One row per action — represents the global default policy plus a roll-up
 * of any employer overrides active for the same action.
 */
interface PolicyRow {
  actionCode: string;
  actionName: string;
  category: PolicyActionCategory | undefined;
  globalPolicy: ActionPolicy;
  lastUpdated: string;
  overrideEmployers: { id: number; name: string | null }[];
}


function isPolicyActiveAt(policy: ActionPolicy, now: Date): boolean {
  const from = new Date(policy.effective_from);
  if (from > now) return false;
  if (policy.effective_until === null) return true;
  return new Date(policy.effective_until) > now;
}


const ActionPoliciesTab: React.FC = () => {
  const [tab, setTab] = useState<Tab>('policies');
  const [policies, setPolicies] = useState<ActionPolicy[]>([]);
  const [actions, setActions] = useState<PolicyAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const history = useHistory();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pols, acts] = await Promise.all([
        verificationClient.listPolicies(),
        verificationClient.listActions(),
      ]);
      setPolicies(pols);
      setActions(acts);
    } catch (err) {
      apiResponseHandler.handleApiError(err);
      setError('Could not load action policies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'policies') load();
  }, [load, tab]);

  const rows: PolicyRow[] = useMemo(() => {
    const now = new Date();
    const actionByCode = new Map(actions.map((a) => [a.code, a]));

    const policiesByAction = new Map<string, ActionPolicy[]>();
    policies.forEach((p) => {
      const arr = policiesByAction.get(p.action_code) || [];
      arr.push(p);
      policiesByAction.set(p.action_code, arr);
    });

    const out: PolicyRow[] = [];
    policiesByAction.forEach((all, actionCode) => {
      const globals = all.filter((p) => p.employer_id === null);
      if (globals.length === 0) return;

      // Latest global default by version_no
      const latestGlobal = globals.reduce(
        (max, p) => (p.version_no > max.version_no ? p : max),
        globals[0]
      );

      // Last updated = max date_created across all global versions for this
      // action. Overrides aren't counted — they're surfaced in their own column.
      const lastUpdated = globals.reduce(
        (max, p) => (p.date_created > max ? p.date_created : max),
        globals[0].date_created
      );

      // Employer IDs (+ names when known) with an active override for this action
      const overrideMap = new Map<number, string | null>();
      all
        .filter((p) => p.employer_id !== null && isPolicyActiveAt(p, now))
        .forEach((p) => {
          const id = p.employer_id as number;
          if (!overrideMap.has(id) || (p.employer_name && !overrideMap.get(id))) {
            overrideMap.set(id, p.employer_name ?? null);
          }
        });
      const overrideEmployers = Array.from(overrideMap.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.id - b.id);

      const action = actionByCode.get(actionCode);

      out.push({
        actionCode,
        actionName: action?.name || '',
        category: action?.category,
        globalPolicy: latestGlobal,
        lastUpdated,
        overrideEmployers,
      });
    });

    return out.sort((a, b) => {
      const aSort = a.category?.sort_order ?? 9999;
      const bSort = b.category?.sort_order ?? 9999;
      if (aSort !== bSort) return aSort - bSort;
      return a.actionCode.localeCompare(b.actionCode);
    });
  }, [policies, actions]);

  const filteredRows = useMemo(() => {
    if (!filter) return rows;
    const q = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.actionCode.toLowerCase().includes(q) ||
        r.actionName.toLowerCase().includes(q) ||
        (r.category?.code || '').toLowerCase().includes(q) ||
        (r.category?.name || '').toLowerCase().includes(q) ||
        r.overrideEmployers.some(
          (e) =>
            (e.name || '').toLowerCase().includes(q) ||
            String(e.id).includes(q)
        )
    );
  }, [rows, filter]);

  const openEmployerOverride = (actionCode: string, employerId: number) => {
    // ActionPolicy is append-only (versioned). Multiple rows can match an
    // action+employer pair — navigate to the highest version_no so admins
    // always land on the live one.
    const override = policies
      .filter((p) => p.action_code === actionCode && p.employer_id === employerId)
      .sort((a, b) => b.version_no - a.version_no)[0];
    if (override) {
      history.push(`/admin/verification/policies/${override.id}`);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Tabs value={tab} onValueChange={(val) => setTab(val as Tab)}>
        <TabsList className="mb-sm">
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
          <TabsTrigger value="requirements">Requirements</TabsTrigger>
          <TabsTrigger value="failed">Failed Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="policies">
          <div className="mb-sm">
            <SearchInput
              placeholder="Filter by action, category, or employer override…"
              value={filter}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
              onClear={() => setFilter('')}
            />
          </div>

          {loading && (
            <div className="flex justify-center py-lg">
              <Loader2 className="animate-spin text-secondary-text-label" size={24} />
            </div>
          )}
          {!loading && error && (
            <EmptyState
              variant="bordered"
              size="sm"
              icon="alert"
              title="Couldn’t load action policies"
              description={error}
              actions={<Button onClick={load}>Try again</Button>}
            />
          )}
          {!loading && !error && filteredRows.length === 0 && (
            <EmptyState
              variant="bordered"
              size="sm"
              icon="search"
              title="No policies match"
              description="No policies match these filters."
            />
          )}
          {!loading && !error && filteredRows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Employer Overrides</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.actionCode}>
                    <TableCell>
                      <div className="flex flex-col">
                        <code className="text-xs">{row.actionCode}</code>
                        {row.actionName && (
                          <span className="text-xs text-greyscale-text-caption">
                            {row.actionName}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.category ? (
                        <Tag variant="outline">{row.category.code}</Tag>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>v{row.globalPolicy.version_no}</TableCell>
                    <TableCell className="text-xs">
                      {new Date(row.lastUpdated).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {row.overrideEmployers.length === 0 ? (
                        <span className="text-xs text-greyscale-text-caption">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-xxs">
                          {row.overrideEmployers.map(({ id: empId, name: empName }) => (
                            <Tooltip key={empId}>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => openEmployerOverride(row.actionCode, empId)}
                                  className="cursor-pointer border-none bg-transparent p-none"
                                  aria-label={`View override for ${empName || `employer ${empId}`}`}
                                >
                                  <Tag variant="pink">{empName || `Employer ${empId}`}</Tag>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>View this override</TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`View ${row.actionCode} policy`}
                            asChild
                          >
                            <Link to={`/admin/verification/policies/${row.globalPolicy.id}`}>
                              <Eye size={16} />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View</TooltipContent>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="actions">
          <ActionsSection />
        </TabsContent>

        <TabsContent value="requirements">
          <RequirementsSection />
        </TabsContent>

        <TabsContent value="failed">
          <FailedActionAttemptsTab />
        </TabsContent>
      </Tabs>
    </TooltipProvider>
  );
};

export default ActionPoliciesTab;
